import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, filter, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { UsersTableComponent } from '../../../components/users-table/users-table.component';
import { PaginationComponent } from '../../../components/pagination/pagination.component';
import { UsersService } from '../../../core/services/users/users.service';
import { DEPARTMENT_LABELS, ROL_LABELS } from '../../../core/constants/user.constants';

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    DropdownModule,
    ProgressSpinnerModule,
    ToastModule,
    UsersTableComponent,
    PaginationComponent
  ],
  providers: [MessageService],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss'
})
export class UsersListComponent implements OnInit, OnDestroy {

  faUserPlus = faUserPlus;

  headers = [
    { name: 'Nombre', key: 'name' },
    { name: 'Email', key: 'email' },
    { name: 'Departamento', key: 'department' },
    { name: 'Roles', key: 'roles' },
    { name: 'Estado', key: 'isActive' }
  ];

  configTable = {
    headers: this.headers,
    data: undefined
  };

  currentPage = 0;
  itemsForPage = 10;
  maxPages = undefined;
  isLoading = false;

  searchName = '';
  selectedDepartment = undefined;
  selectedStatus = undefined;
  selectedRole = undefined;
  isRefreshing = false;

  private nameSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  departments = DEPARTMENT_LABELS;
  roles = ROL_LABELS;
  statusOptions = [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false }
  ];

  constructor(
    private usersService: UsersService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  async ngOnInit() {
    this.isLoading = true;

    this.nameSearch$.pipe(
      debounceTime(350),
      // Solo si el texto difiere de lo último consultado; distinctUntilChanged se desincronizaba al limpiar con la X.
      filter(() => this.searchName !== this.lastFetchedName),
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      this.currentPage = 0;
      this.updateQueryParams();
      await this.getData();
    });

    const qp = this.route.snapshot.queryParams;
    if (qp) {
      this.currentPage = Number.isInteger(Number(qp['page'])) && Number(qp['page']) > 0 ? Number(qp['page']) : 0;
      this.searchName = qp['name'] || '';
      this.selectedDepartment = qp['department'] || undefined;
      this.selectedStatus = qp['isActive'] !== undefined ? qp['isActive'] === 'true' : undefined;
      this.selectedRole = qp['rol'] || undefined;
    }

    try {
      await this.getData();
    } finally {
      this.isLoading = false; // un fallo en la primera carga no debe dejar el spinner para siempre
    }
  }

  private lastRequestId = 0;
  private lastFetchedName = '';

  async getData() {
    const requestId = ++this.lastRequestId; // descartar respuestas viejas si se cambia de filtro rápido
    this.lastFetchedName = this.searchName;
    this.isRefreshing = true;
    let data;
    try {
      data = await this.usersService.getUsers(
        this.currentPage,
        this.itemsForPage,
        this.searchName || undefined,
        this.selectedDepartment,
        this.selectedRole,
        this.selectedStatus
      );
    } catch (error) {
      console.error('Error loading users:', error);
      if (requestId === this.lastRequestId) {
        this.isRefreshing = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el listado de usuarios' });
      }
      return;
    }

    if (requestId !== this.lastRequestId) return;
    this.maxPages = data.totalPages;
    this.configTable = { ...this.configTable, headers: this.headers, data: data.users };
    this.isLoading = false;
    this.isRefreshing = false;
  }

  get hasFilters(): boolean {
    return !!this.searchName || !!this.selectedDepartment || !!this.selectedRole || this.selectedStatus !== undefined;
  }

  private updateQueryParams() {
    const queryParams: any = {
      page: this.currentPage ?? 0,
      name: this.searchName || null,
      department: this.selectedDepartment ?? null,
      rol: this.selectedRole ?? null,
      isActive: this.selectedStatus !== undefined ? this.selectedStatus : null
    };
    this.router.navigate([], { relativeTo: this.route, queryParams, queryParamsHandling: 'merge' });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createUser() {
    const queryParams = { ...this.route.snapshot.queryParams };
    this.router.navigate(['/users/detail/new'], { queryParams });
  }

  onNameSearch() {
    this.nameSearch$.next(this.searchName);
  }

  async onNameCleared() {
    this.searchName = '';
    this.currentPage = 0;
    this.updateQueryParams();
    await this.getData();
  }

  async onDepartmentSelected(event: any) {
    this.selectedDepartment = event.value;
    this.currentPage = 0;
    this.updateQueryParams();
    await this.getData();
  }

  async onDepartmentCleared() {
    this.selectedDepartment = undefined;
    this.currentPage = 0;
    this.updateQueryParams();
    await this.getData();
  }

  async onRoleChanged(role?: string) {
    this.selectedRole = role || undefined;
    this.currentPage = 0;
    this.updateQueryParams();
    await this.getData();
  }

  async clearFilters() {
    this.searchName = '';
    this.selectedDepartment = undefined;
    this.selectedRole = undefined;
    this.selectedStatus = undefined;
    this.currentPage = 0;
    this.updateQueryParams();
    await this.getData();
  }

  async onStatusSelected(event: any) {
    this.selectedStatus = event.value;
    this.currentPage = 0;
    this.updateQueryParams();
    await this.getData();
  }

  async onStatusCleared() {
    this.selectedStatus = undefined;
    this.currentPage = 0;
    this.updateQueryParams();
    await this.getData();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.updateQueryParams();
    this.getData();
  }
}
